import { useState, useMemo } from "react";
import moment from "moment-timezone";
import {
  useListFreightImages,
  useGetFreightImageUpload,
  FreightImageUpload,
  getFreightImageStatusLabel,
  getFreightImageStatusColor,
  FreightImageUploadStatusType,
  isStatusFinalDownloadable,
} from "../../lib/api/freight/index";
import { useListCustomers } from "../../lib/api/customer";
import { DataTable } from "../../components/ui/DataTable/DataTable";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { MultiSelect, SelectItem as MultiSelectItem } from "../../components/ui/selectMulti";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { DataTableColumn } from "@/components/ui/DataTable/types";
import { Eye, Download, ExternalLink } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { downloadFromS3 } from "@/lib/file";


// Component for action buttons
function ImageActionsCell({
  uploadId,
  status,
  onPreview,
}: {
  uploadId: string;
  status: FreightImageUploadStatusType;
  onPreview: () => void;
}) {
  const { toast } = useToast();
  const [isLoadingAction, setIsLoadingAction] = useState(false);
  
  const { refetch } = useGetFreightImageUpload({
    uploadId,
    enabled: false, // Never auto-fetch
  });
  const willHaveAnLoadableImage = isStatusFinalDownloadable(status);

  const handleAction = async (action: "preview" | "download" | "open") => {
    setIsLoadingAction(true);
    
    try {
      const result = await refetch();
      
      if (!result.data?.success) {
        toast({
          title: "Failed to load image",
          description: "Could not retrieve image details.",
          variant: "destructive",
        });
        return;
      }

      const presignedUrl = result.data.data.presigned_image_url;

      if (!presignedUrl) {
        toast({
          title: "Image not available",
          description: "The image file could not be found or is not ready yet.",
          variant: "destructive",
        });
        return;
      }

      switch (action) {
        case "preview":
          onPreview();
          break;
        case "download": {
          const filename =
            result.data.data.final_document?.filename ||
            "image";
          const downloadResult = await downloadFromS3(presignedUrl, filename);
          if (downloadResult.success) {
            toast({
              title: "Download started",
              description: "Your image is being downloaded.",
            });
          } else {
            toast({
              title: "Download failed",
              description: "Failed to download the image. Please try again.",
              variant: "destructive",
            });
          }
          break;
        }
        case "open":
          window.open(presignedUrl, "_blank");
          break;
      }
    } finally {
      setIsLoadingAction(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => handleAction("preview")}
        disabled={willHaveAnLoadableImage || isLoadingAction}
        title="Preview image"
      >
        <Eye className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => handleAction("download")}
        disabled={willHaveAnLoadableImage || isLoadingAction}
        title="Download image"
      >
        <Download className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => handleAction("open")}
        disabled={willHaveAnLoadableImage || isLoadingAction}
        title="Open in new tab"
      >
        <ExternalLink className="h-4 w-4" />
      </Button>
    </div>
  );
}

export function FreightImagesDashboard() {
  const [limit] = useState(50);
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<FreightImageUploadStatusType | undefined>();
  const [previewImageId, setPreviewImageId] = useState<string | null>(null);

  // Fetch customers for filter
  const customersRes = useListCustomers({ minimal: true });

  // Build query params for freight images
  const queryParams = useMemo(() => {
    const params: {
      limit: number;
      date_from?: string;
      date_to?: string;
      status?: FreightImageUploadStatusType;
      customer_ids?: string;
    } = { limit };

    if (dateFrom) {
      params.date_from = dateFrom;
    }
    if (dateTo) {
      params.date_to = dateTo;
    }
    if (statusFilter) {
      params.status = statusFilter;
    }
    if (selectedCustomerIds.length > 0) {
      params.customer_ids = selectedCustomerIds.join(",");
    }

    return params;
  }, [limit, dateFrom, dateTo, statusFilter, selectedCustomerIds]);

  // Fetch freight images
  const freightImagesRes = useListFreightImages(queryParams);

  const customers = customersRes.data?.success ? customersRes.data.data : [];

  // Convert customers to MultiSelect options
  const customerOptions: MultiSelectItem[] = useMemo(() => {
    return customers.map((customer: { id: string; long_name: string }) => ({
      value: customer.id,
      label: customer.long_name,
    }));
  }, [customers]);

  const data = freightImagesRes.data?.success ? freightImagesRes.data.data.uploads : [];

  const columns: DataTableColumn<FreightImageUpload>[] = [
    {
      accessorKey: "created_at",
      header: "Upload Date",
      cell: ({ row }) => {
        return (
          <span className="text-sm">
            {moment
              .tz(row.original.created_at, "America/Chicago")
              .format("MM/DD/YYYY HH:mm a")}
          </span>
        );
      },
    },
    {
      accessorKey: "relation_customer",
      header: "Customer",
      cell: ({ row }) => {
        const customer = row.original.relation_customer;
        return (
          <span className="text-sm">
            {customer ? customer.long_name : "--"}
          </span>
        );
      },
    },
      /* {
  *   accessorKey: "final_document",
  *   header: "Filename",
  *   cell: ({ row }) => {
  *     const finalDoc = row.original.final_document;
  *     const doc = finalDoc;
  *     return (
  *       <span className="text-sm font-mono">
  *         {doc ? doc.filename : "N/A"}
  *       </span>
  *     );
  *   },
  * }, */
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <Badge className={getFreightImageStatusColor(status)}>
            {getFreightImageStatusLabel(status)}
          </Badge>
        );
      },
    },
    {
      accessorKey: "status_message",
      header: "Message",
      cell: ({ row }) => {
        const message = row.original.status_message;
        const subtype = row.original.status_subtype;
        return (
          <span className="text-sm text-gray-600">
            {message || subtype || "—"}
          </span>
        );
      },
    },
    {
      accessorKey: "actions",
      header: "Actions",
      cell: ({ row }) => {
        return (
          <ImageActionsCell
            status={row.original.status}
            uploadId={row.original.id}
            onPreview={() => setPreviewImageId(row.original.id)}
          />
        );
      },
    },
  ];

  // Only show skeleton on initial customer load (needed for filters)
  if (customersRes.isLoading && !customersRes.data) {
    return (
      <div className="flex flex-col space-y-4 p-4">
        <Skeleton className="h-8 w-[300px]" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (customersRes.error) {
    return (
      <div className="flex flex-col space-y-2 p-4">
        <div className="text-red-600">
          Failed to load customers. Please refresh and try again.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Freight Images</h1>
        <p className="text-sm text-gray-500 mt-1">
          View and manage freight image uploads
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Date From */}
          <div className="space-y-2">
            <Label htmlFor="date-from">Date From</Label>
            <Input
              id="date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              placeholder="YYYY-MM-DD"
            />
          </div>

          {/* Date To */}
          <div className="space-y-2">
            <Label htmlFor="date-to">Date To</Label>
            <Input
              id="date-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              placeholder="YYYY-MM-DD"
            />
          </div>

          {/* Status Filter */}
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select
              value={statusFilter || "all"}
              onValueChange={(value) => {
                if (value === "all") {
                  setStatusFilter(undefined);
                } else {
                  setStatusFilter(value as FreightImageUploadStatusType);
                }
              }}
            >
              <SelectTrigger id="status">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending_upload">Pending Upload</SelectItem>
                <SelectItem value="uploaded">Uploaded</SelectItem>
                <SelectItem value="scanning">Scanning</SelectItem>
                <SelectItem value="quarantined">Quarantined</SelectItem>
                <SelectItem value="resizing">Resizing</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Customer Filter */}
          <div className="space-y-2">
            <Label htmlFor="customer">Customers</Label>
            <MultiSelect
              values={selectedCustomerIds}
              setValues={setSelectedCustomerIds}
              options={customerOptions}
            />
          </div>
        </div>

        {/* Clear Filters */}
        {(dateFrom || dateTo || statusFilter || selectedCustomerIds.length > 0) && (
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setDateFrom("");
                setDateTo("");
                setStatusFilter(undefined);
                setSelectedCustomerIds([]);
              }}
            >
              Clear All Filters
            </Button>
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="text-sm text-gray-600">
        {freightImagesRes.isLoading ? (
          "Loading..."
        ) : (
          <>
            Showing {data.length} of{" "}
            {freightImagesRes.data?.success
              ? freightImagesRes.data.data.pagination.total
              : 0}{" "}
            total images
          </>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <DataTable
          data={data}
          columns={columns}
          enableSorting={true}
          enableFiltering={false}
          rowClickable={false}
          emptyMessage={
            freightImagesRes.isLoading
              ? "Loading freight images..."
              : freightImagesRes.error
              ? "Failed to load freight images. Please try again."
              : "No freight images found"
          }
        />
      </div>

      {/* Preview Modal */}
      <ImagePreviewModal
        uploadId={previewImageId}
        onClose={() => setPreviewImageId(null)}
      />
    </div>
  );
}

// Preview Modal Component
function ImagePreviewModal({
  uploadId,
  onClose,
}: {
  uploadId: string | null;
  onClose: () => void;
}) {
  const { data, isLoading } = useGetFreightImageUpload({
    uploadId: uploadId || "",
    enabled: !!uploadId,
  });

  const presignedUrl = data?.success ? data.data.presigned_image_url : null;
  const filename = data?.success
    ? data.data.final_document?.filename
    : null;

  return (
    <Dialog open={!!uploadId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>{filename || "Image Preview"}</DialogTitle>
        </DialogHeader>
        <div className="flex items-center justify-center min-h-[400px]">
          {isLoading ? (
            <div className="text-gray-500">Loading image...</div>
          ) : !presignedUrl ? (
            <div className="text-red-600">
              Image not available. The file could not be found or is not ready yet.
            </div>
          ) : (
            <img
              src={presignedUrl}
              alt={filename || "Freight image"}
              className="max-w-full max-h-[70vh] object-contain"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
